import React, { useState, useRef, useEffect } from 'react';
import { X, Download, Square, AlertCircle, CheckCircle2 } from 'lucide-react';
import { createSeasonImporter } from '../lib/seasonImport';
import { gamesDB } from '../lib/supabase';

/**
 * APEX v7.6.0 — Season Import Modal
 *
 * Modal d'import de toutes les games de la saison 2026 (depuis le 4 janvier).
 * 2 boutons : SoloQ et Flex. Throttling automatique, Stop possible, ETA en temps réel.
 *
 * Props :
 *   - onClose            : callback de fermeture
 *   - puuid              : PUUID du joueur
 *   - platform           : 'europe' (default 'europe')
 *   - existingGames      : array de games déjà en DB (pour le dedupe)
 *   - onGameImported     : callback(parsedGame) appelé pour chaque game importée
 *                          (le caller fait gamesDB.add + setGames)
 */
export default function SeasonImportModal({
  onClose,
  puuid,
  platform = 'europe',
  existingGames = [],
  onGameImported
}) {
  const [queue, setQueue] = useState(null); // null | 420 | 440
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({
    phase: 'idle',
    current: 0,
    total: 0,
    fetchedIds: 0,
    skippedDedupe: 0,
    skippedPatch: 0,
    imported: 0,
    errors: 0,
    etaMs: null
  });
  const importerRef = useRef(null);

  // Cleanup si on ferme la modal pendant un import
  useEffect(() => {
    return () => {
      if (importerRef.current) {
        importerRef.current.abort();
      }
    };
  }, []);

  const handleStart = async (q) => {
    if (!puuid) return;
    setQueue(q);
    setRunning(true);

    // Construit le Set des match_ids déjà en DB pour le dedupe
    const existingIds = new Set(
      existingGames
        .map(g => g.riot_match_id || g.data?.riot_match_id)
        .filter(Boolean)
    );

    const importer = createSeasonImporter({
      puuid,
      platform,
      queue: q,
      existingMatchIds: existingIds,
      onProgress: (state) => setProgress(state),
      onGame: async (parsedGame) => {
        try {
          const saved = await gamesDB.add(parsedGame);
          onGameImported?.(saved);
        } catch (e) {
          console.error('[SeasonImportModal] save error:', e);
        }
      }
    });

    importerRef.current = importer;
    await importer.run();
    setRunning(false);
  };

  const handleStop = () => {
    if (importerRef.current) {
      importerRef.current.abort();
    }
  };

  // Format ETA
  const formatEta = (ms) => {
    if (!ms || ms < 1000) return '~';
    const totalSec = Math.ceil(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    if (min > 0) return `~ ${min}min ${sec}s`;
    return `~ ${sec}s`;
  };

  const isDone = progress.phase === 'done';
  const isAborted = progress.phase === 'aborted';
  const isError = progress.phase === 'error';
  const isImporting = progress.phase === 'importing';
  const isListing = progress.phase === 'listing';
  const isRateLimited = progress.phase === 'rate_limited';

  const percent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={running ? undefined : onClose}
    >
      <div
        className="relative bg-zinc-950 border border-zinc-800 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Download size={16} className="text-cyan-400" />
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-cyan-400">
              Import Saison Complète
            </div>
          </div>
          {!running && (
            <button
              onClick={onClose}
              className="text-zinc-500 hover:text-zinc-300"
              aria-label="Fermer"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">

          {/* État idle : 2 boutons de choix */}
          {progress.phase === 'idle' && (
            <>
              <div className="text-sm text-zinc-300">
                Importe <span className="text-white font-bold">toutes</span> tes games de la saison 2026 (depuis le 4 janvier).
              </div>
              <div className="text-[11px] text-zinc-500 font-mono">
                • Les games déjà importées sont ignorées<br />
                • Les games hors saison (patch ≠ 16.x) sont filtrées<br />
                • Durée estimée : ~6 min pour 200 games, ~12 min pour 400 games<br />
                • Tu peux fermer cette modale et le STOP arrêtera proprement
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  onClick={() => handleStart(420)}
                  disabled={!puuid}
                  className="px-4 py-3 border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 font-bold text-sm tracking-wider transition disabled:opacity-30"
                >
                  SOLOQ
                </button>
                <button
                  onClick={() => handleStart(440)}
                  disabled={!puuid}
                  className="px-4 py-3 border-2 border-fuchsia-400 text-fuchsia-400 hover:bg-fuchsia-400/10 font-bold text-sm tracking-wider transition disabled:opacity-30"
                >
                  FLEX
                </button>
              </div>

              {!puuid && (
                <div className="text-[10px] text-amber-400 font-mono flex items-center gap-1.5">
                  <AlertCircle size={12} />
                  Compte Riot non lié — lie ton PUUID dans les settings
                </div>
              )}
            </>
          )}

          {/* État listing : on récupère la liste des match IDs */}
          {isListing && (
            <>
              <div className="text-sm text-zinc-300">
                Récupération de la liste des games...
              </div>
              <div className="text-[11px] text-zinc-500 font-mono">
                Queue : <span className="text-white">{queue === 420 ? 'SoloQ' : 'Flex'}</span><br />
                Match IDs trouvés : <span className="text-cyan-400 font-bold">{progress.fetchedIds}</span>
              </div>
              {/* Indeterminate progress bar */}
              <div className="h-1.5 bg-zinc-900 overflow-hidden">
                <div
                  className="h-full bg-cyan-400"
                  style={{
                    width: '30%',
                    animation: 'apexListingPulse 1.2s ease-in-out infinite'
                  }}
                />
              </div>
              <style>{`
                @keyframes apexListingPulse {
                  0% { transform: translateX(-100%); }
                  100% { transform: translateX(400%); }
                }
              `}</style>
            </>
          )}

          {/* État rate limit : pause backoff */}
          {isRateLimited && (
            <div className="border border-amber-400/40 bg-amber-400/5 px-4 py-3 text-center">
              <div className="text-sm font-bold text-amber-400 mb-1">
                Limite Riot atteinte — pause
              </div>
              <div className="text-[11px] text-zinc-400">
                L'API Riot a renvoyé un rate limit. L'import reprend automatiquement
                dans ~30s, sans rien perdre. Tu peux laisser la fenêtre ouverte.
              </div>
              <div className="text-[10px] text-zinc-500 font-mono mt-2">
                {progress.imported} games importées · {progress.current}/{progress.total}
              </div>
            </div>
          )}

          {/* État importing : barre de progression + ETA + stats */}
          {isImporting && (
            <>
              <div className="flex items-center justify-between text-sm">
                <div className="text-zinc-300">
                  Import en cours <span className="text-zinc-500 font-mono text-[10px]">({queue === 420 ? 'SoloQ' : 'Flex'})</span>
                </div>
                <div className="text-cyan-400 font-mono font-bold">
                  {progress.current} / {progress.total}
                </div>
              </div>

              {/* Barre de progression */}
              <div className="h-2 bg-zinc-900 border border-zinc-800 overflow-hidden">
                <div
                  className="h-full bg-cyan-400 transition-all duration-300"
                  style={{
                    width: `${percent}%`,
                    boxShadow: '0 0 10px rgba(34, 211, 238, 0.5)'
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3 text-[11px] font-mono">
                <div>
                  <span className="text-zinc-500">Progression : </span>
                  <span className="text-white">{percent}%</span>
                </div>
                <div className="text-right">
                  <span className="text-zinc-500">Temps restant : </span>
                  <span className="text-white">{formatEta(progress.etaMs)}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Importées : </span>
                  <span className="text-cyan-400 font-bold">{progress.imported}</span>
                </div>
                <div className="text-right">
                  <span className="text-zinc-500">Hors saison : </span>
                  <span className="text-zinc-400">{progress.skippedPatch}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Déjà en DB : </span>
                  <span className="text-zinc-400">{progress.skippedDedupe}</span>
                </div>
                <div className="text-right">
                  <span className="text-zinc-500">Erreurs : </span>
                  <span className={progress.errors > 0 ? 'text-rose-400' : 'text-zinc-400'}>
                    {progress.errors}
                  </span>
                </div>
              </div>

              <button
                onClick={handleStop}
                className="w-full px-4 py-2 border border-rose-400/40 text-rose-400 hover:bg-rose-400/10 font-bold text-xs tracking-wider transition flex items-center justify-center gap-2"
              >
                <Square size={14} /> ARRÊTER
              </button>

              <div className="text-[10px] text-zinc-500 font-mono">
                Tu peux arrêter à tout moment. Les games déjà importées sont sauvegardées.
              </div>
            </>
          )}

          {/* État done */}
          {isDone && (
            <>
              <div className="flex items-center gap-3">
                <CheckCircle2 size={24} className="text-cyan-400" />
                <div>
                  <div className="text-sm text-white font-bold">Import terminé</div>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    {progress.imported} games ajoutées · {progress.skippedDedupe} déjà en DB · {progress.skippedPatch} hors saison
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 font-bold text-sm tracking-wider transition"
              >
                FERMER
              </button>
            </>
          )}

          {/* État aborted */}
          {isAborted && (
            <>
              <div className="flex items-center gap-3">
                <Square size={24} className="text-amber-400" />
                <div>
                  <div className="text-sm text-white font-bold">Import arrêté</div>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    {progress.imported} games sauvegardées avant l'arrêt
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 font-mono">
                Tu peux relancer l'import : les games déjà sauvegardées seront ignorées.
              </div>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 border border-zinc-700 text-zinc-300 hover:bg-zinc-900 font-bold text-sm tracking-wider transition"
              >
                FERMER
              </button>
            </>
          )}

          {/* État error */}
          {isError && (
            <>
              <div className="flex items-center gap-3">
                <AlertCircle size={24} className="text-rose-400" />
                <div>
                  <div className="text-sm text-white font-bold">Erreur pendant l'import</div>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    {progress.imported} games sauvegardées · {progress.errors} erreurs
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 font-mono">
                Consulte la console pour le détail. Tu peux relancer l'import : les games déjà sauvegardées seront ignorées.
              </div>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 border border-zinc-700 text-zinc-300 hover:bg-zinc-900 font-bold text-sm tracking-wider transition"
              >
                FERMER
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
